import { useMutation, useQuery, useQueryClient } from "@tanstack/vue-query";
import { trackReportFeature } from "@/analytics/report-analytics";
import APIClient from "@/APIClient";
import { QueryKey } from "./query-keys";
import { useAIGuardError } from "@/composable/useAIGuardError";
import type { CreateSectionPayload } from "@/types/sections.types";
import type { Ref } from "vue";

export const useCreateSection = () => {
  const queryClient = useQueryClient();
  const { createErrorHandler } = useAIGuardError();

  return useMutation({
    mutationFn: async (payload: CreateSectionPayload) => {
      return await APIClient.createSection(payload);
    },
    onSuccess: () => {
      trackReportFeature("section_created");
      queryClient.invalidateQueries({
        queryKey: [QueryKey.SECTIONS],
      });
    },
    onError: createErrorHandler("toast:fieldObservations.partCreateError"),
  });
};

export const useUpdateSection = (id: Ref<number>) => {
  const queryClient = useQueryClient();
  const { createErrorHandler } = useAIGuardError();

  return useMutation({
    mutationFn: async (payload: { title: string }) => {
      return await APIClient.updateSection(id.value, payload);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [QueryKey.SECTIONS],
      });
    },
    onError: createErrorHandler("toast:sections.update-error"),
  });
};

export const useMoveSection = () => {
  const queryClient = useQueryClient();
  const { createErrorHandler } = useAIGuardError();

  return useMutation({
    mutationFn: async (payload: {
      newOrder: number;
      newParent: number | null;
      id: number;
    }) => {
      return await APIClient.moveSection(
        payload.id,
        payload.newOrder,
        payload.newParent,
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [QueryKey.SECTIONS],
      });
    },
    onError: createErrorHandler("toast:sections.move-error"),
  });
};

export const useAddFindingToSection = (sectionId: Ref<number>) => {
  const queryClient = useQueryClient();
  const { createErrorHandler } = useAIGuardError();

  return useMutation({
    mutationFn: async (fileId: number) => {
      return await APIClient.addImageToSection(sectionId.value, fileId);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [QueryKey.FINDINGS, { section: sectionId.value }],
      });
    },
    onError: createErrorHandler("toast:sections.add-image-error"),
  });
};

export const useGetSectionFindings = (sectionId: Ref<number>) => {
  return useQuery({
    queryKey: [QueryKey.FINDINGS, { section: sectionId }],
    queryFn: async () => {
      return await APIClient.fetchSectionFindings(sectionId.value);
    },
  });
};

export const useDeleteSection = (id: Ref<number>) => {
  const queryClient = useQueryClient();
  const { createErrorHandler } = useAIGuardError();

  return useMutation({
    mutationFn: async () => {
      return await APIClient.deleteSection(id.value);
    },
    onSuccess: () => {
      trackReportFeature("section_deleted");
      queryClient.invalidateQueries({
        queryKey: [QueryKey.SECTIONS],
      });
    },
    onError: createErrorHandler("toast:sections.delete-error"),
  });
};

export const useDuplicateSection = (id: Ref<number>) => {
  const queryClient = useQueryClient();
  const { createErrorHandler } = useAIGuardError();

  return useMutation({
    mutationFn: async () => {
      return await APIClient.duplicateSection(id.value);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [QueryKey.SECTIONS],
      });
    },
    onError: createErrorHandler("toast:sections.duplicate-error"),
  });
};

export const useApplySectionTemplate = () => {
  const queryClient = useQueryClient();
  const { createErrorHandler } = useAIGuardError();

  return useMutation({
    mutationFn: async (payload: {
      reportId: number;
      templateId: number;
      parentSectionId?: number | null;
    }) => {
      return await APIClient.applySectionTemplate(
        payload.reportId,
        payload.templateId,
        payload.parentSectionId,
      );
    },
    onSuccess: () => {
      trackReportFeature("sections_template_inserted");
      queryClient.invalidateQueries({
        queryKey: [QueryKey.SECTIONS],
      });
    },
    onError: createErrorHandler("toast:fieldObservations.applyTemplateError"),
  });
};

export const useSaveSectionsAsTemplate = () => {
  const queryClient = useQueryClient();
  const { createErrorHandler } = useAIGuardError();

  return useMutation({
    mutationFn: async (payload: {
      reportId: number;
      name: string;
      description?: string;
      parentSectionId?: number | null;
    }) => {
      return await APIClient.saveSectionsAsTemplate(
        payload.reportId,
        payload.name,
        payload.description,
        payload.parentSectionId,
      );
    },
    onSuccess: () => {
      trackReportFeature("sections_template_saved");
      queryClient.invalidateQueries({
        queryKey: [QueryKey.FIELD_PART_TEMPLATES],
      });
    },
    onError: createErrorHandler("toast:sections.save-template-error"),
  });
};

export const useCreateSectionsFromCatalogue = (
  diagnosticId: Ref<number>,
  sectionId: Ref<number | null>,
) => {
  const queryClient = useQueryClient();
  const { createErrorHandler } = useAIGuardError();

  return useMutation({
    mutationFn: async (catalogueId: number) => {
      return await APIClient.createSectionsFromCatalogue(
        diagnosticId.value,
        sectionId.value,
        catalogueId,
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [QueryKey.SECTIONS],
      });
    },
    onError: createErrorHandler("toast:sections.create-from-catalogue-error"),
  });
};
